import { WipExtractionReviewStatus } from "@inrev/common";
import { match } from "ts-pattern";
import type { IconType } from "../../../../primitives";

export const WIP_REVIEW_TIMESTAMP_FORMAT = "MM/dd/yyyy h:mm a";

export type WipReviewDisplayState =
	| "not-started"
	| "pending"
	| "failed"
	| "reviewed"
	| "in-review"
	| "needs-review"
	| "high-confidence";

const DISPLAY_LABELS: Record<WipReviewDisplayState, string> = {
	"not-started": WipExtractionReviewStatus.displayName(WipExtractionReviewStatus.NotStarted),
	pending: WipExtractionReviewStatus.displayName(WipExtractionReviewStatus.Pending),
	failed: WipExtractionReviewStatus.displayName(WipExtractionReviewStatus.Failed),
	reviewed: WipExtractionReviewStatus.displayName(WipExtractionReviewStatus.Reviewed),
	"in-review": "In Review",
	"needs-review": WipExtractionReviewStatus.displayName(WipExtractionReviewStatus.NeedsReview),
	"high-confidence": WipExtractionReviewStatus.displayName(
		WipExtractionReviewStatus.HighConfidence,
	),
};

export function resolveWipReviewDisplayState(
	reviewStatus: WipExtractionReviewStatus,
	hasCorrections: boolean,
): WipReviewDisplayState {
	if (
		hasCorrections &&
		(reviewStatus === WipExtractionReviewStatus.NeedsReview ||
			reviewStatus === WipExtractionReviewStatus.HighConfidence)
	) {
		return "in-review";
	}

	return match(reviewStatus)
		.with(WipExtractionReviewStatus.NotStarted, (): WipReviewDisplayState => "not-started")
		.with(WipExtractionReviewStatus.Pending, (): WipReviewDisplayState => "pending")
		.with(WipExtractionReviewStatus.Failed, (): WipReviewDisplayState => "failed")
		.with(WipExtractionReviewStatus.Reviewed, (): WipReviewDisplayState => "reviewed")
		.with(WipExtractionReviewStatus.NeedsReview, (): WipReviewDisplayState => "needs-review")
		.with(WipExtractionReviewStatus.HighConfidence, (): WipReviewDisplayState => "high-confidence")
		.exhaustive();
}

export function wipReviewDisplayLabel(
	reviewStatus: WipExtractionReviewStatus,
	hasCorrections: boolean,
): string {
	const displayState = resolveWipReviewDisplayState(reviewStatus, hasCorrections);
	return DISPLAY_LABELS[displayState];
}

export function wipReviewStatusIconClass(displayState: WipReviewDisplayState): string {
	return match(displayState)
		.with("reviewed", () => "border-confirm-hover bg-surface-confirm text-confirm-hover")
		.with("failed", () => "border-danger-hover bg-surface-base text-danger-hover")
		.with(
			"needs-review",
			() => "border-intent-warning-border bg-intent-warning-bg text-intent-warning-text",
		)
		.with("pending", () => "border-brand-primary bg-surface-base text-brand-primary")
		.with("not-started", () => "border-bc-heavy bg-surface-base text-secondary")
		.with(
			"high-confidence",
			() => "border-intent-warning-border bg-intent-warning-bg text-intent-warning-text",
		)
		.with("in-review", () => "border-brand-primary bg-surface-base text-brand-primary")
		.exhaustive();
}

export function wipReviewStatusIconType(displayState: WipReviewDisplayState): IconType {
	return match(displayState)
		.with("reviewed", (): IconType => "check")
		.with("failed", (): IconType => "x-circle")
		.with("needs-review", (): IconType => "exclamation-triangle")
		.with("pending", (): IconType => "clock")
		.with("not-started", (): IconType => "minus-circle")
		.with("high-confidence", (): IconType => "exclamation-triangle")
		.with("in-review", (): IconType => "pencil")
		.exhaustive();
}
